import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getSupabaseUrl } from '../supabase/client';

interface QuillEditorWithImageResizeProps {
  value: string;
  onChange: (content: string, widths?: number[]) => void;
  questionId: string;
  imageWidths?: number[];
  onImageWidthsChange?: (widths: number[]) => void;
}

interface ImageSizeState {
  scale: number;
  rect: DOMRect;
  display: 'inline' | 'block';
}

function processImageUrl(src: string): string {
  if (!src) return '';
  if (src.startsWith('http://') || src.startsWith('https://')) return src;
  if (src.startsWith('/storage/')) return `${getSupabaseUrl()}${src}`;
  if (src.startsWith('/sb-api/')) return src;
  return src;
}

function processHtmlImages(html: string): string {
  if (!html) return html;
  return html.replace(/<img([^>]+)src="([^"]+)"([^>]*)>/g, (match, before, src, after) => {
    return `<img${before}src="${processImageUrl(src)}"${after}>`;
  });
}

// 将imageWidths数组注入到HTML中的img标签style属性
// 如果widths数组长度不足，用100%作为默认值
function injectWidthsIntoHtml(html: string, widths: number[]): string {
  if (!html) return html;
  const hasImages = html.includes('<img');
  if (!hasImages) return html;
  let imgIndex = 0;
  return html.replace(/<img([^>]*?)>/g, (match, attrs) => {
    const width = imgIndex < widths.length ? widths[imgIndex] : 100;
    imgIndex++;
    if (attrs.includes('style="')) {
      return match.replace(/style="([^"]*)"/, (m, styleContent) => {
        const newStyle = styleContent.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
        return `style="width:${width}%;${newStyle}"`;
      });
    }
    return `<img${attrs} style="width:${width}%;">`;
  });
}

// 从HTML中的img标签提取宽度数组
function extractWidthsFromHtml(html: string): number[] {
  if (!html) return [];
  const widths: number[] = [];
  const imgRegex = /<img[^>]*>/g;
  let imgMatch;
  while ((imgMatch = imgRegex.exec(html)) !== null) {
    const imgTag = imgMatch[0];
    const styleMatch = imgTag.match(/style="[^"]*width:\s*(\d+)%[^"]*"/);
    const widthAttrMatch = imgTag.match(/width="(\d+)%"/);
    const width = styleMatch ? parseInt(styleMatch[1]) : (widthAttrMatch ? parseInt(widthAttrMatch[1]) : 100);
    widths.push(width);
  }
  return widths;
}

export default function QuillEditorWithImageResize({
  value,
  onChange,
  questionId,
  imageWidths = [],
  onImageWidthsChange,
}: QuillEditorWithImageResizeProps) {
  const quillRef = useRef<ReactQuill>(null);
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  const [imageSize, setImageSize] = useState<ImageSizeState | null>(null);
  const [showSlider, setShowSlider] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);
  const prevQuestionIdRef = useRef(questionId);

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: { matchVisual: false }
  };

  const formats = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'align', 'link', 'image'
  ];

  // 非受控模式：questionId变化时（切换题目），重新设置编辑器内容
  // defaultValue只在首次挂载时生效，切换题目需要手动同步
  useEffect(() => {
    if (prevQuestionIdRef.current !== questionId) {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      const htmlWithWidths = injectWidthsIntoHtml(processHtmlImages(value), imageWidths);
      editor.clipboard.dangerouslyPasteHTML(htmlWithWidths || '<p></p>');
      prevQuestionIdRef.current = questionId;
      setSelectedImage(null);
      setShowSlider(false);
    }
  }, [questionId]);

  // 当imageWidths外部变化时（非缩放操作导致的），应用到DOM
  useEffect(() => {
    if (isResizingRef.current) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const images = editor.root.querySelectorAll('img');
    if (images.length === 0 || imageWidths.length === 0) return;

    images.forEach((img, index) => {
      if (index < imageWidths.length) {
        img.style.width = `${imageWidths[index]}%`;
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
      }
    });
  }, [imageWidths]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInEditor = target.closest('.quill-editor-wrapper');
      const isInSlider = target.closest('.image-size-slider');
      const isImage = target.tagName === 'IMG';
      if (isImage) return;
      if (isInEditor && !isImage) {
        setSelectedImage(null);
        setShowSlider(false);
        document.querySelectorAll('.ql-editor img').forEach((img) => img.classList.remove('selected'));
        return;
      }
      if (!isInEditor && !isInSlider) {
        setSelectedImage(null);
        setShowSlider(false);
        document.querySelectorAll('.ql-editor img').forEach((img) => img.classList.remove('selected'));
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const editorElement = editor.root;

    const handleImageClick = (e: Event) => {
      const target = e.target as HTMLElement;
      let imgElement: HTMLImageElement | null = null;

      if (target.tagName === 'IMG') {
        imgElement = target as HTMLImageElement;
      } else {
        const clickX = (e as MouseEvent).clientX;
        const clickY = (e as MouseEvent).clientY;
        const allImages = editorElement.querySelectorAll('img');
        let closestImg: HTMLImageElement | null = null;
        let minDistance = Infinity;
        allImages.forEach((img) => {
          const rect = img.getBoundingClientRect();
          const imgCenterX = rect.left + rect.width / 2;
          const imgCenterY = rect.top + rect.height / 2;
          const distance = Math.sqrt(Math.pow(clickX - imgCenterX, 2) + Math.pow(clickY - imgCenterY, 2));
          if (clickX >= rect.left - 10 && clickX <= rect.right + 10 &&
              clickY >= rect.top - 10 && clickY <= rect.bottom + 10) {
            if (distance < minDistance) {
              minDistance = distance;
              closestImg = img as HTMLImageElement;
            }
          }
        });
        imgElement = closestImg;
      }

      if (!imgElement) return;
      e.preventDefault();
      e.stopPropagation();

      setShowSlider(false);
      setSelectedImage(null);

      setTimeout(() => {
        editorElement.querySelectorAll('img').forEach((img) => img.classList.remove('selected'));
        imgElement.classList.add('selected');
        setSelectedImage(imgElement);

        // 从DOM style获取当前宽度
        const currentWidth = parseInt(imgElement.style.width) || 100;
        const computedStyle = window.getComputedStyle(imgElement);
        const display = computedStyle.display === 'block' ? 'block' : 'inline';
        const rect = imgElement.getBoundingClientRect();
        setImageSize({ scale: currentWidth, rect, display: display as 'inline' | 'block' });
        setInputValue(String(currentWidth));
        setShowSlider(true);
      }, 10);
    };

    editorElement.addEventListener('click', handleImageClick, true);
    return () => { editorElement.removeEventListener('click', handleImageClick, true); };
  }, [questionId]);

  useEffect(() => {
    if (!showSlider || !selectedImage) return;
    const updatePosition = () => {
      const rect = selectedImage.getBoundingClientRect();
      setImageSize(prev => prev ? { ...prev, rect } : null);
    };
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showSlider, selectedImage]);

  // 拖拽滑块时：直接修改DOM样式（视觉反馈）
  // 非受控模式下，DOM修改不会被ReactQuill重新渲染覆盖
  const handleScaleChange = useCallback((newScale: number) => {
    if (selectedImage) {
      isResizingRef.current = true;
      selectedImage.style.width = `${newScale}%`;
      selectedImage.style.maxWidth = '100%';
      selectedImage.style.height = 'auto';
      setImageSize(prev => prev ? { ...prev, scale: newScale } : null);
      setInputValue(String(newScale));
    }
  }, [selectedImage]);

  // 手动输入框值变化时：实时同步滑块和图片宽度
  const handleInputChange = useCallback((val: string) => {
    setInputValue(val);
    const num = parseInt(val);
    if (!isNaN(num) && num >= 20 && num <= 200 && selectedImage) {
      isResizingRef.current = true;
      selectedImage.style.width = `${num}%`;
      selectedImage.style.maxWidth = '100%';
      selectedImage.style.height = 'auto';
      setImageSize(prev => prev ? { ...prev, scale: num } : null);
    }
  }, [selectedImage]);

  // 松开滑块时：合并title和titleImageWidths为单次调用，避免React异步状态更新覆盖
  const handleScaleChangeComplete = useCallback(() => {
    if (!isResizingRef.current) return;

    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // 从DOM中提取所有img的当前宽度
    const allImages = editor.root.querySelectorAll('img');
    const newWidths: number[] = [];
    allImages.forEach((img) => {
      const w = parseInt(img.style.width) || 100;
      newWidths.push(w);
    });

    // 获取当前HTML，将宽度注入到img style中
    const currentHtml = editor.root.innerHTML;
    const htmlWithWidths = injectWidthsIntoHtml(processHtmlImages(currentHtml), newWidths);

    // 合并title和titleImageWidths为单次onChange调用，确保原子性更新
    // 避免两次独立updateQuestion调用导致React异步状态覆盖
    onChange(htmlWithWidths, newWidths);

    setTimeout(() => {
      isResizingRef.current = false;
    }, 100);
  }, [onChange]);

  // 输入框失焦时：确认最终值并持久化
  const handleInputBlur = useCallback(() => {
    const num = parseInt(inputValue);
    if (isNaN(num) || num < 20 || num > 200) {
      const currentScale = imageSize?.scale || 100;
      setInputValue(String(currentScale));
      return;
    }
    handleScaleChangeComplete();
  }, [inputValue, imageSize, handleScaleChangeComplete]);

  // ReactQuill的onChange：处理图片URL并传递content
  // 注意：不从content中提取宽度，因为Quill规范化后的HTML可能不含width style
  // 宽度信息由handleScaleChangeComplete通过DOM提取并通知父组件
  const handleChange = (content: string) => {
    if (isResizingRef.current) return;
    onChange(processHtmlImages(content));
  };

  // 非受控模式：使用defaultValue，不使用value
  // 这样ReactQuill不会在每次父组件state更新时重新渲染覆盖DOM修改
  const processedDefaultValue = injectWidthsIntoHtml(processHtmlImages(value), imageWidths);

  return (
    <div ref={containerRef} className="relative">
      <div className="quill-editor-wrapper">
        <ReactQuill
          ref={quillRef}
          defaultValue={processedDefaultValue}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder="请输入题目内容，支持富文本格式..."
          className="bg-white"
        />
      </div>

      {showSlider && imageSize && selectedImage && (
        <div
          ref={sliderRef}
          className="image-size-slider"
          style={{
            position: 'fixed',
            left: `${Math.max(10, Math.min(imageSize.rect.left + imageSize.rect.width / 2 - 120, window.innerWidth - 260))}px`,
            top: `${Math.max(10, imageSize.rect.bottom + 10)}px`,
            zIndex: 9999
          }}
        >
          <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg min-w-[240px]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">图片缩放</span>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="20"
                  max="200"
                  value={inputValue}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onBlur={handleInputBlur}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInputBlur(); } }}
                  className="w-12 h-5 text-xs text-center border border-gray-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                />
                <span className="text-xs text-gray-500">%</span>
              </div>
            </div>
            <input
              type="range"
              min="20"
              max="200"
              value={imageSize.scale}
              onChange={(e) => handleScaleChange(parseInt(e.target.value))}
              onMouseUp={handleScaleChangeComplete}
              onTouchEnd={handleScaleChangeComplete}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(imageSize.scale - 20) / 1.8}%, #e5e7eb ${(imageSize.scale - 20) / 1.8}%, #e5e7eb 100%)`
              }}
            />
            <div className="flex justify-between mt-1 text-xs text-gray-400">
              <span>20%</span>
              <span>200%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}